/**
 * Deduplicated classification of merge-request terminal work outcomes.
 * List/detail polling can re-deliver the same terminal row; callers claim once
 * so cache invalidation and analytics fire a single time per terminal signature.
 */

import type { MergeRequestStatus, MergeWorkKind, MergeWorkStatus } from './types'

export type MergeWorkTerminalKind = 'preview_succeeded' | 'apply_succeeded'

export interface MergeWorkTerminalFields {
  id: string
  status: MergeRequestStatus | string
  sourceId?: string
  targetId?: string
  workKind?: MergeWorkKind | string
  workStatus?: MergeWorkStatus | string
  resultCommitHash?: string
}

const claimed = new Set<string>()

function terminalSignature(mr: MergeWorkTerminalFields, kind: MergeWorkTerminalKind): string {
  return `${mr.id}:${mr.workStatus ?? ''}:${mr.status}:${mr.resultCommitHash ?? ''}:${kind}`
}

/** Classify a merge-request row as a terminal success, or null if not. */
export function classifyMergeWorkTerminal(
  mr: MergeWorkTerminalFields
): MergeWorkTerminalKind | null {
  if (mr.status === 'merged' || (mr.workKind === 'apply' && mr.workStatus === 'succeeded')) {
    return 'apply_succeeded'
  }
  if (mr.workKind === 'preview' && mr.workStatus === 'succeeded') {
    return 'preview_succeeded'
  }
  return null
}

/**
 * Claim a terminal success for side effects. Returns the kind on the first
 * claim for this signature; subsequent polls of the same terminal return null.
 */
export function claimMergeWorkTerminal(mr: MergeWorkTerminalFields): MergeWorkTerminalKind | null {
  const kind = classifyMergeWorkTerminal(mr)
  if (!kind) return null
  const sig = terminalSignature(mr, kind)
  if (claimed.has(sig)) return null
  claimed.add(sig)
  return kind
}

/** Test helper: clear claimed signatures between cases. */
export function resetMergeWorkTerminalClaims(): void {
  claimed.clear()
}
